import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Checkbox, Divider, Form, Input, Space, Steps, Tooltip, message } from "antd";
import { CheckboxSpan, Container, ContainerHeader, ContainerTitle, GroupLabel, PermissionCheckbox, PermissionGroupWrapper, RoleConfigDivider, RoleConfigWrapper } from "./styles";
import StyledModal from "../../components/CustomModal";
import CustomTable from "../../components/CustomTable";
import ConfirmationModal from "../../components/ConfirmationModal";
import { DeleteOutlined, EditOutlined, EyeOutlined } from "@ant-design/icons";
import { useAppDispatch, useAppSelector } from "../../utils/hooks/redux";
import { createRole, deleteUserRole, getAllPermissionConfig, getAllRolesConfig, updateRole } from "../../redux/features/user/userAsyncThunk";
import { GetAllUserRolesResponse, UserPermissionConfig } from "../../redux/features/user/types";
import { FooterButtons, StyledButton, FormWrapper } from "../../components/styles";
import { toast } from "react-toastify";
import { selectUsers } from "../../redux/features/user/userSlice";
import { permissions } from "../../utils/constants/permissions";
import { checkHasPermission } from "../../utils/helpers/checkHasPermission";
import { selectUserRole } from "../../redux/features/auth/selectors";
import { PermissionConfigFor } from "../../redux/features/auth/IAuthState";

const UserRoleConfigurationPage: React.FC = () => {
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [roleDetails, setRoleDetails] = useState<GetAllUserRolesResponse | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const dispatch = useAppDispatch();
  const { allUserPermissions, roles, loading: rolesLoading } = useAppSelector(selectUsers);
  const userRole = useAppSelector(selectUserRole);

  const configurationGroup = useMemo(() => {
    const groups = [
      {
        key: PermissionConfigFor.MenuVisibility,
        label: "Menu Visibility",
        configType: PermissionConfigFor.MenuVisibility,
        configuration: allUserPermissions?.filter((permission) => permission?.configType === PermissionConfigFor.MenuVisibility).sort((a, b) => a?.index - b?.index) || [],
      },
      {
        key: PermissionConfigFor.SystemManagement,
        label: "System Management",
        configType: PermissionConfigFor.SystemManagement,
        configuration: allUserPermissions?.filter((permission) => permission?.configType === PermissionConfigFor.SystemManagement).sort((a, b) => a?.index - b?.index) || [],
      },
      {
        key: PermissionConfigFor.PaymentManagement,
        label: "Payment Management",
        configType: PermissionConfigFor.PaymentManagement,
        configuration: allUserPermissions?.filter((permission) => permission?.configType === PermissionConfigFor.PaymentManagement).sort((a, b) => a?.index - b?.index) || [],
      },
      {
        key: PermissionConfigFor.StatusPermissions,
        label: "Status Permissions",
        configType: PermissionConfigFor.StatusPermissions,
        configuration: allUserPermissions?.filter((permission) => permission?.configType === PermissionConfigFor.StatusPermissions).sort((a, b) => a?.index - b?.index) || [],
      },
    ];
    return groups;
  }, [allUserPermissions]);

  const handleGetUserConfig = async () => {
    dispatch(getAllRolesConfig());
    dispatch(getAllPermissionConfig());
  };

  useEffect(() => {
    handleGetUserConfig();
  }, []);

  const openModal = (role?: GetAllUserRolesResponse) => {
    setRoleDetails(role || null);
    setIsModalOpen(true);
    if (role) {
      const permissionMap: { [key: string]: string[] } = {};

      // Group permission configs by configType
      role.permissionConfigs?.forEach((config) => {
        const groupKey = `permissions_group_${config.configType}`;
        if (!permissionMap[groupKey]) {
          permissionMap[groupKey] = [];
        }
        permissionMap[groupKey].push(config.id);
      });

      const formData = {
        name: role.name,
        description: role.description,
        isActive: role.isActive,
        ...permissionMap,
      };

      setFormData(formData);
      form.setFieldsValue(formData);
    } else {
      setFormData(null);
      form.resetFields();
      form.setFieldsValue({ isActive: false });
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    form.resetFields();
    setCurrentStep(0);
    setFormData(null);
    setRoleDetails(null);
  };

  const [formData, setFormData] = useState<any>(null);

  const hasPermission = useCallback((permissionKey: string) => checkHasPermission(userRole, permissionKey), [userRole]);

  const isAddPermission = useMemo(() => hasPermission(permissions.AddEditRoles), [hasPermission]);
  const isEditPermission = useMemo(() => hasPermission(permissions.AddEditRoles), [hasPermission]);
  const isViewPermission = useMemo(() => hasPermission(permissions.ViewRoles), [hasPermission]);
  const isDeletePermission = useMemo(() => hasPermission(permissions.DeleteRoles), [hasPermission]);

  const onFinish = async (values: any) => {
    setLoading(true);

    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

    const permissionConfigs = Object.entries(values)
      .map(([key, value]: [string, any]) => (key.includes("permissions_group") ? value?.map((permission: any) => permission) : []))
      .flat()
      .filter((val): val is string => typeof val === 'string' && uuidRegex.test(val));

    const roleConfigPayload = {
      name: values.name ?? formData?.name,
      description: values.description ?? formData?.description,
      isActive: values.isActive ?? formData?.isActive ?? false,
      permissionConfigs,
    };
    try {
      if (roleDetails?.id) {
        await dispatch(updateRole({ id: roleDetails.id, data: roleConfigPayload })).unwrap();
        toast.success("Role Updated successfully!");
      } else {
        await dispatch(createRole({ data: roleConfigPayload })).unwrap();
        toast.success("Role created successfully!");
      }
      closeModal();
    } catch (error: any) {
      console.error(error);
      toast.error(error || (roleDetails?.id ? "Failed to update role" : "Failed to create role"));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRole = async () => {
    if (roleDetails?.id) {
      setLoading(true);
      try {
        await dispatch(deleteUserRole({ id: roleDetails.id }));
        setRoleDetails(null);
        setShowDeleteConfirm(false);
        message.success("Role deleted successfully!");
      } catch (error) {
        console.error(error);
        message.error("Failed to delete role");
      } finally {
        setLoading(false);
      }
    }
  };

  const columns = useMemo(() => {
    return [
      { title: "Role Name", dataIndex: "name", key: "name" },
      { title: "Description", dataIndex: "description", key: "description" },
      {
        title: "Active",
        dataIndex: "isActive",
        key: "isActive",
        render: (isActive: boolean) => (isActive ? "Yes" : "No"),
      },
      {
        title: "Operation",
        key: "operation",
        render: (_: any, record: GetAllUserRolesResponse) => (
          <Space>
            {isEditPermission && <Button size="small" onClick={() => openModal(record)} type="link" icon={<EditOutlined />} />}
            {isDeletePermission && (
              <Button
                size="small"
                onClick={() => {
                  setRoleDetails(record);
                  setShowDeleteConfirm(true);
                }}
                type="link"
                danger
                icon={<DeleteOutlined />}
              />
            )}
            {isViewPermission && !isAddPermission && !isEditPermission && <Button size="small" icon={<EyeOutlined />} onClick={() => openModal(record)} type="link" />}
          </Space>
        ),
      },
    ];
  }, [roles, isDeletePermission, isEditPermission, isViewPermission, isAddPermission]);

  const steps = [
    {
      title: "Basic Info",
      content: (
        <>
          <Form.Item label="Role Name" name="name" rules={[{ required: true, message: "Please enter role name" }]}>
            <Input disabled={!isEditPermission || !isAddPermission} size="large" />
          </Form.Item>
          <Form.Item label="Description" name="description" rules={[{ required: true, message: "Please enter description" }]}>
            <Input.TextArea disabled={!isEditPermission || !isAddPermission} autoSize={{ minRows: 3, maxRows: 5 }} />
          </Form.Item>
          <Form.Item name="isActive" valuePropName="checked">
            <Checkbox disabled={!isEditPermission || !isAddPermission}>Active</Checkbox>
          </Form.Item>
        </>
      ),
    },
    {
      title: "User Role Configuration",
      content: (
        <RoleConfigWrapper>
          {configurationGroup.map((group, index) => (
            <>
              <Form.Item style={{ marginBottom: 0 }} key={group.key} name={`permissions_group_${group.configType}`} label={<GroupLabel>{group.label}</GroupLabel>}>
                <Checkbox.Group style={{ width: "100%" }} disabled={!isEditPermission || !isAddPermission}>
                  <PermissionGroupWrapper>
                    {group.configuration.map(({ id, configName }: UserPermissionConfig) => (
                      <PermissionCheckbox className="checkbox-item" key={id}
                        onClick={() => {
                          const currentValues = form.getFieldValue(`permissions_group_${group.configType}`) || [];
                          const isChecked = currentValues.includes(id);
                          const newValue = isChecked
                            ? currentValues.filter((value: string) => value !== id)
                            : [...currentValues, id];
                          form.setFieldValue(`permissions_group_${group.configType}`, newValue);
                        }}
                      >
                        <Checkbox value={id} onClick={(e) => e.stopPropagation()} />
                        <Tooltip title={configName}>
                          <CheckboxSpan title={configName}>{configName}</CheckboxSpan>
                        </Tooltip>
                      </PermissionCheckbox>
                    ))}
                  </PermissionGroupWrapper>
                </Checkbox.Group>
              </Form.Item>
              {index !== configurationGroup.length - 1 && <RoleConfigDivider variant="solid" />}
            </>
          ))}
        </RoleConfigWrapper>
      ),
    },
  ];

  return (
    <>
      <Container>
        <ContainerHeader>
          <ContainerTitle>User Role Configuration</ContainerTitle>
          {isAddPermission && (
            <StyledButton onClick={() => openModal()} type="primary">
              Add Role
            </StyledButton>
          )}
        </ContainerHeader>
      </Container>

      <Container>
        <CustomTable<GetAllUserRolesResponse> pagination={false} columns={columns} dataSource={roles} loading={rolesLoading} />
      </Container>

      {isModalOpen && (
        <StyledModal
          draggable={true}
          width={"80%"}
          minHeight={"100%"}
          footer={
            <FooterButtons>
              {currentStep > 0 ? <StyledButton onClick={() => setCurrentStep(currentStep - 1)}>Previous</StyledButton> : <StyledButton onClick={closeModal}>Cancel</StyledButton>}
              {currentStep < steps.length - 1 && (
                <StyledButton
                  onClick={() => {
                    form
                      .validateFields()
                      .then((res) => {
                        // if (roleDetails) {
                        //   form.setFieldsValue({
                        //     name: roleDetails.name,
                        //     description: roleDetails.description,
                        //     isActive: roleDetails.isActive,
                        //     permissionConfigs: roleDetails.permissionConfigs?.map((config) => config.id) || [],
                        //   });
                        // }
                        setFormData((prev: any) => ({ ...prev, ...res }));
                        setCurrentStep(currentStep + 1);
                      })
                      .catch(() => { });
                  }}
                  type="primary"
                  disabled={modalLoading}
                >
                  Next
                </StyledButton>
              )}
              {currentStep === steps.length - 1 && isAddPermission && (
                <StyledButton onClick={() => form.submit()} loading={loading} type="primary">
                  Save
                </StyledButton>
              )}
            </FooterButtons>
          }
          title="Role Configuration"
          open={isModalOpen}
          centered
          onCancel={closeModal}
        >
          {modalLoading ? (
            <p>Loading...</p>
          ) : (
            <FormWrapper form={form} layout="vertical" onFinish={onFinish}>
              <Steps current={currentStep}>
                {steps.map((step) => (
                  <Steps.Step key={step.title} title={step.title} />
                ))}
              </Steps>
              <div style={{ margin: "20px 0" }}>{steps[currentStep].content}</div>
            </FormWrapper>
          )}
        </StyledModal>
      )}

      {showDeleteConfirm && (
        <ConfirmationModal
          handleSave={handleDeleteRole}
          isModalOpen={showDeleteConfirm}
          onCancel={() => setShowDeleteConfirm(false)}
          title={"Confirm Delete?"}
          message={`Are you sure you want to delete this role?`}
          confirmButtonText="Delete"
          loading={loading}
          hasEditPermission={isEditPermission}
          hasViewPermission={isViewPermission}
          hasAddPermission={isAddPermission}
        />
      )}
    </>
  );
};

export default UserRoleConfigurationPage;
